/**
 * Scroll positions remembered per history entry — the `scroll` namespace of the
 * shared store.
 *
 * The keying and pruning rules, and why vue-router's own `scrollBehavior` cannot
 * do this, live in `historyMemory.ts`. This module exists to pair those keys with
 * the one payload shape a scroll container needs, so callers cannot accidentally
 * read a map camera out of a scroll slot.
 */

import {
  historyKey,
  positionOfState,
  readHistoryEntry,
  resetHistoryMemory,
  syncNavigation,
  writeHistoryEntry,
} from '@/utils/historyMemory'

/** One remembered container. `state` is an opaque payload owned by the screen. */
export interface ScrollEntry {
  top: number
  state?: unknown
}

const NAMESPACE = 'scroll'

/**
 * Argument order is `(position, fullPath, id)` rather than `historyKey`'s
 * `(position, namespace, id, fullPath)` — kept as it was so every existing caller
 * reads unchanged.
 */
export function scrollKey(position: number, fullPath: string, id: string): string {
  return historyKey(position, NAMESPACE, id, fullPath)
}

export function readEntry(key: string): ScrollEntry | undefined {
  return readHistoryEntry<ScrollEntry>(key)
}

export function writeEntry(key: string, entry: ScrollEntry): void {
  writeHistoryEntry(key, entry)
}

export { positionOfState, syncNavigation }

/**
 * Test-only. Clears every namespace, not just this one: the payloads are keyed by
 * the same history entry, so a test that wants scroll state gone wants the map
 * camera for that entry gone too.
 */
export function resetScrollMemory(): void {
  resetHistoryMemory()
}
